/**
 * מסך המשחק — סגנון Travian
 * ================================================================
 * 3 מסכי-משנה עיקריים בטאבים:
 *   MAP       — מפת העולם, כל הערים, החצרות, צעדים אפשריים
 *   DASHBOARD — החצר שלך: משאבים, מוסדות, תור בנייה, שלוחים
 *   RANKINGS  — דירוגי החצרות בעולם
 */

import { useEffect, useRef, useState } from 'react';
import * as api from '../api/client.js';
import { MapView } from './round/MapView.js';
import { Dashboard } from './round/Dashboard.js';
import { Rankings } from './round/Rankings.js';
import { TopBar } from './round/TopBar.js';
import { NotificationsPanel } from './round/NotificationsPanel.js';

// המבנה הרזה של World שהלקוח קורא
export interface WorldState {
  meta: {
    tickNumber: number;
    currentYear: number;
    currentDay: number;
  };
  entities: {
    dynasties: Record<string, Dyn>;
    characters: Record<string, Char>;
    cities: Record<string, City>;
    countries: Record<string, { id: string; name: string; region: string }>;
    institutions: Record<string, Institution>;
    events: Record<string, EventInst>;
  };
  log: LogEntry[];
  followers: Follower[];
}

export interface Dyn {
  id: string;
  name: string;
  resources: Record<string, number>;
  reputation: number;
  isPlayer: boolean;
  currentAdamorId: string | null;
  homeCityId: string;
  ethnicity: string;
  stream: string;
  status: string;
  aiPersonality?: string;
}
export interface Char {
  id: string;
  displayName: string;
  dynastyId: string | null;
  role: string;
  status: string;
  birthTick: number;
  gender: string;
  homeCityId: string;
  careerPath?: string;
  careerProgress?: { fame: number; level: number };
}
export interface City {
  id: string;
  name: string;
  countryId: string;
  x: number;
  y: number;
  jewishPopulation: number;
  totalPopulation: number;
  isHolyPlace: boolean;
  hasChareidiMunicipality?: boolean;
  sizeTier: string;
}
export interface Institution {
  id: string;
  dynastyId: string;
  type: string;
  level: number;
  cityId: string;
  status: string;
  founded: number;
}
export interface EventInst {
  id: string;
  templateId: string;
  targetDynastyId: string;
  status: string;
  context: Record<string, unknown>;
  triggeredAt: number;
}
export interface LogEntry {
  tick: number;
  category: string;
  message: string;
  relatedIds: string[];
}
export interface Follower {
  cityId: string;
  dynastyId: string;
  count: number;
  loyalty: number;
}

export type Tab = 'map' | 'dashboard' | 'rankings';

interface RoundData {
  round: api.RoundSummary;
  player: api.RoundPlayer | null;
  state: WorldState | null;
}

export function RoundScreen({
  roundId,
  onLeave,
}: {
  roundId: string;
  onLeave: () => void;
}) {
  const [data, setData] = useState<RoundData | null>(null);
  const [tab, setTab] = useState<Tab>('map');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  const lastLoadedRef = useRef<number>(0);

  const load = async (): Promise<void> => {
    try {
      const res = await api.getRoundState(roundId);
      setData({
        round: res.round,
        player: res.player,
        state: res.state as WorldState | null,
      });
      lastLoadedRef.current = Date.now();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 5000); // רענון כל 5 שניות
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId]);

  const submitIntent = async (
    intentType: string,
    payload: Record<string, unknown>,
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      await api.submitIntent(roundId, intentType, payload);
      await load();
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'שגיאה';
      return { success: false, message: msg };
    }
  };

  if (loading) return <div className="app-loading">טוען משחק...</div>;
  if (error) {
    return (
      <div className="app-error">
        <div>שגיאה: {error}</div>
        <button onClick={onLeave}>חזרה ל-Lobby</button>
      </div>
    );
  }
  if (!data) return <div>אין נתונים</div>;
  const { round, player, state } = data;

  if (!player) {
    return (
      <div className="app-error">
        <div>אתה לא רשום למשחק הזה.</div>
        <button onClick={onLeave}>חזרה ל-Lobby</button>
      </div>
    );
  }
  if (round.status === 'waiting') {
    return (
      <div className="app-loading">
        <h2>{round.name}</h2>
        <p>המשחק ממתין לתחילה. חזור אחר כך.</p>
        <button onClick={onLeave}>חזרה ל-Lobby</button>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="app-loading">
        <div>ממתין למצב המשחק...</div>
        <button onClick={onLeave}>חזרה</button>
      </div>
    );
  }

  const playerDynasty = state.entities.dynasties[player.dynasty_id];

  return (
    <div className="round-view">
      <TopBar
        round={round}
        state={state}
        player={player}
        playerDynasty={playerDynasty}
        onLeave={onLeave}
        tab={tab}
        onTabChange={setTab}
      />

      <div className="round-main">
        {tab === 'map' && (
          <MapView
            state={state}
            player={player}
            selectedCityId={selectedCityId}
            onSelectCity={setSelectedCityId}
            onIntent={submitIntent}
          />
        )}

        {tab === 'dashboard' && playerDynasty && (
          <Dashboard
            state={state}
            player={player}
            dynasty={playerDynasty}
            onIntent={submitIntent}
          />
        )}

        {tab === 'rankings' && (
          <Rankings state={state} player={player} />
        )}

        <NotificationsPanel state={state} player={player} />
      </div>
    </div>
  );
}
